<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import LeadTaskTypesAPI from 'dashboard/api/masifi/leadTaskTypes';
import { unwrap } from '../../leads/helper';

const { t } = useI18n();
const dialog = ref(null);
const deleteDialog = ref(null);
const types = ref([]);
const originalNames = ref({});
const newName = ref('');
const isSaving = ref(false);
const typeToDelete = ref(null);

const load = async () => {
  try {
    const { data } = await LeadTaskTypesAPI.get();
    types.value = unwrap(data);
    originalNames.value = Object.fromEntries(
      types.value.map(ty => [ty.id, ty.name])
    );
  } catch (error) {
    types.value = [];
  }
};

const open = async () => {
  await load();
  newName.value = '';
  dialog.value?.open();
};
defineExpose({ open });

const add = async () => {
  const name = newName.value.trim();
  if (!name) return;
  isSaving.value = true;
  try {
    await LeadTaskTypesAPI.create({ name, position: types.value.length + 1 });
    newName.value = '';
    await load();
  } catch (error) {
    useAlert(t('LEADS.AGENDA.TYPES.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const rename = async type => {
  const name = (type.name || '').trim();
  if (!name || name === originalNames.value[type.id]) return;
  try {
    await LeadTaskTypesAPI.update(type.id, { name });
    originalNames.value[type.id] = name;
    useAlert(t('LEADS.AGENDA.TYPES.SAVED'));
  } catch (error) {
    useAlert(t('LEADS.AGENDA.TYPES.SAVE_ERROR'));
    await load();
  }
};

const confirmRemove = type => {
  typeToDelete.value = type;
  deleteDialog.value?.open();
};

const remove = async () => {
  if (!typeToDelete.value) return;
  try {
    await LeadTaskTypesAPI.delete(typeToDelete.value.id);
    await load();
  } catch (error) {
    useAlert(t('LEADS.AGENDA.TYPES.DELETE_ERROR'));
  } finally {
    typeToDelete.value = null;
  }
};

const move = async (index, delta) => {
  const target = index + delta;
  if (target < 0 || target >= types.value.length) return;
  const reordered = [...types.value];
  const [item] = reordered.splice(index, 1);
  reordered.splice(target, 0, item);
  types.value = reordered;
  try {
    await LeadTaskTypesAPI.reorder(
      reordered.map((ty, i) => ({ id: ty.id, position: i + 1 }))
    );
  } catch (error) {
    useAlert(t('LEADS.AGENDA.TYPES.SAVE_ERROR'));
    await load();
  }
};
</script>

<template>
  <div>
    <Dialog
      ref="dialog"
      :title="t('LEADS.AGENDA.TYPES.TITLE')"
      :show-confirm-button="false"
    >
      <div class="flex flex-col gap-3">
        <div
          v-if="!types.length"
          class="py-4 text-sm text-center text-n-slate-10"
        >
          {{ t('LEADS.AGENDA.TYPES.EMPTY') }}
        </div>
        <ul class="flex flex-col gap-1.5">
          <li
            v-for="(type, index) in types"
            :key="type.id"
            class="flex items-center gap-1.5"
          >
            <Input v-model="type.name" class="flex-1" @blur="rename(type)" />
            <Button
              ghost
              slate
              xs
              icon="i-lucide-arrow-up"
              @click="move(index, -1)"
            />
            <Button
              ghost
              slate
              xs
              icon="i-lucide-arrow-down"
              @click="move(index, 1)"
            />
            <Button
              ghost
              ruby
              xs
              icon="i-lucide-trash-2"
              @click="confirmRemove(type)"
            />
          </li>
        </ul>
        <div class="flex items-center gap-1.5 pt-2 border-t border-n-weak">
          <Input
            v-model="newName"
            class="flex-1"
            :placeholder="t('LEADS.AGENDA.TYPES.NAME_PLACEHOLDER')"
            @keyup.enter="add"
          />
          <Button
            blue
            sm
            icon="i-lucide-plus"
            :label="t('LEADS.AGENDA.TYPES.ADD')"
            :is-loading="isSaving"
            @click="add"
          />
        </div>
      </div>
    </Dialog>

    <!-- Confirmación de borrado -->
    <Dialog
      ref="deleteDialog"
      type="alert"
      :title="t('LEADS.AGENDA.TYPES.DELETE_TITLE')"
      :description="t('LEADS.AGENDA.TYPES.DELETE_CONFIRM')"
      :confirm-button-label="t('LEADS.AGENDA.TYPES.DELETE')"
      @confirm="remove"
    />
  </div>
</template>
